import React, { useEffect } from "react";
import { Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MobileBottomNav from "@/components/catalog/MobileBottomNav";
import VoiceWidget from "@/components/chat/VoiceWidget";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const SUPPORTED_LANGS = ["en", "fr", "de", "it", "pt", "es"];
const BASE_URL = "https://myhorsevault.com";
const STORAGE_KEY = "i18nextLng";
const RESET_FLAG_KEY = "langResetV2";

// One-time reset for historical visitors stuck on /de from the previous default.
// If no reset flag is present and the stored language is a "manual" choice (no
// auto: prefix), wipe it and redirect to root so LangRedirect re-runs detection.
function consumeOneTimeReset(currentLang: string | undefined): string | null {
  if (typeof window === "undefined") return null;
  try {
    if (window.localStorage.getItem(RESET_FLAG_KEY)) return null;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    window.localStorage.setItem(RESET_FLAG_KEY, "1");
    window.sessionStorage.removeItem("geoLangChecked");
    if (saved && !saved.startsWith("auto:")) {
      window.localStorage.removeItem(STORAGE_KEY);
      // Force a fresh detection cycle
      return "/";
    }
  } catch {}
  return null;
}

const LanguageLayout: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Set language and html lang attribute. If the user has a manually-chosen
  // preference that differs from the URL segment, redirect to the preferred lang.
  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang)) {
      const resetTo = consumeOneTimeReset(lang);
      if (resetTo) {
        navigate(resetTo, { replace: true });
        return;
      }

      let preferred: string | null = null;
      try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (saved && !saved.startsWith("auto:")) {
          const short = saved.toLowerCase().split("-")[0];
          if (SUPPORTED_LANGS.includes(short)) preferred = short;
        }
      } catch {}

      if (preferred && preferred !== lang) {
        const newPath = location.pathname.replace(`/${lang}`, `/${preferred}`);
        navigate(`${newPath}${location.search}${location.hash}`, { replace: true });
        return;
      }

      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      document.documentElement.lang = lang;
    } else if (lang && !SUPPORTED_LANGS.includes(lang)) {
      const rest = location.pathname.replace(`/${lang}`, "") || "/";
      navigate(`/fr${rest}${location.search}`, { replace: true });
    }
  }, [lang, i18n, navigate, location]);

  // Inject hreflang <link> tags and canonical into <head>
  useEffect(() => {
    const pathWithoutLang = lang
      ? location.pathname.replace(`/${lang}`, "") || ""
      : "";

    // Remove old hreflang links and canonical
    document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());

    // Skip hreflang on paginated pages (query params like ?page=2)
    const hasQueryParams = location.search.includes("page=");

    if (!hasQueryParams) {
      SUPPORTED_LANGS.forEach(l => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = l;
        link.href = `${BASE_URL}/${l}${pathWithoutLang}`;
        link.setAttribute("data-hreflang", "true");
        document.head.appendChild(link);
      });

    // x-default → French (primary market)
    const xDefault = document.createElement("link");
    xDefault.rel = "alternate";
    xDefault.hreflang = "x-default";
    xDefault.href = `${BASE_URL}/fr${pathWithoutLang}`;
    xDefault.setAttribute("data-hreflang", "true");
    document.head.appendChild(xDefault);
    }

    // Self-referencing canonical
    let canonical = document.querySelector('link[rel="canonical"][data-managed]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      canonical.setAttribute("data-managed", "true");
      document.head.appendChild(canonical);
    }
    // Canonical should exclude query params for clean URLs
    canonical.setAttribute("href", `${BASE_URL}/${lang || "fr"}${pathWithoutLang}`);

    return () => {
      document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
    };
  }, [lang, location.pathname, location.search]);

  return (
    <>
      <SiteHeader />
      <main id="main-content" role="main" className="pb-16 sm:pb-0">
        <Outlet />
      </main>
      <SiteFooter />
      <MobileBottomNav />
      <VoiceWidget />
    </>
  );
};

export { SUPPORTED_LANGS };
export default LanguageLayout;
